import { useEffect, useState } from 'react'
import * as sentry from '@sentry/browser'
import {
  Manifest,
  Artboard,
  getConnectedArtboards,
} from '@sketch-hq/sketch-prototype-player'

import { getImagesToPreloadFromArtboardList } from './getImagesToPreloadFromArtboardList'
import { ImagesPreloader } from 'components/Image'

function isDefinedArtboard(
  artboard: undefined | Artboard
): artboard is Artboard {
  return !!artboard
}

type ImagePreloadStatus = 'idle' | 'loading' | 'completed' | 'error'

type UsePrototypeImagesPreloadProps = {
  manifest: Manifest | null
  resolveImageSrc: ((path: string) => string) | null
  initialArtboardId: string
}

export function usePrototypeImagesPreload(
  props: UsePrototypeImagesPreloadProps
) {
  const { manifest, resolveImageSrc, initialArtboardId } = props
  const [imageLoadProgress, setImageLoadProgress] = useState(0)
  const [imagesCount, setImagesCount] = useState(0)

  const [
    imagePreloadStatus,
    setImagePreloadStatus,
  ] = useState<ImagePreloadStatus>('idle')

  useEffect(() => {
    if (!manifest || !resolveImageSrc) {
      return
    }

    const entryArtboard = manifest.contents.pages
      .flatMap(page => page.artboards)
      .find(artboard => artboard.id === initialArtboardId)

    const artboards = [
      entryArtboard,
      ...getConnectedArtboards(manifest, initialArtboardId),
    ].filter(isDefinedArtboard)

    const imagesToPreload = getImagesToPreloadFromArtboardList(artboards)

    setImagePreloadStatus('loading')
    const imagePreloader = new ImagesPreloader(imagesToPreload, resolveImageSrc)
    imagePreloader.addOnProgressListener(progress => {
      setImageLoadProgress(progress)
    })

    imagePreloader
      .whenReady()
      .then(result => {
        if (result.failed !== 0) {
          setImagePreloadStatus('error')
          sentry.addBreadcrumb({ message: 'Prototype image preloading failed' })
        } else {
          setImagesCount(imagesToPreload.length)
          setImagePreloadStatus('completed')
        }
      })
      .catch(() => {
        // Catch cancel promises after component has unmounted.
        // Do nothing.
      })

    return () => {
      imagePreloader.dispose()
      setImageLoadProgress(0)
      setImagePreloadStatus('idle')
    }
    /**
     * It's important this useEffect only runs once
     */
  }, [manifest, resolveImageSrc, initialArtboardId])

  return {
    imagePreloadProgress: imageLoadProgress,
    imagePreloadStatus,
    imagesCount,
  }
}
